import type { Chapter } from '../model/playback.ts';
import {
  SPONSOR_CATEGORIES,
  type ResolvedSponsorSegment,
  type SponsorBlockSettings,
  type SponsorCategory,
} from './model.ts';

/**
 * What to *do* about the segments — the per-tick skip decision, the bar geometry and the chapter
 * badges. All pure over plain data, for `playback/chrome.ts`'s reason: Compose has to draw and
 * decide exactly the same things on TV, and a version living in a hook is a version Android
 * re-derives from scratch.
 */

export const SPONSORBLOCK_DEFAULT_API_URL = 'https://sponsor.ajay.app';

/**
 * The k-anonymity lookup URL. The prefix is the first 4 hex characters of SHA-256(videoId) —
 * computed by the platform, since core cannot hash — so the service learns a ~150-video bucket
 * rather than the video (measured: prefix `305b` answered 156 videos).
 *
 * All eight categories are always requested, whatever the settings say: the parameter filters the
 * whole *bucket*, so it reveals nothing about the video, and asking for everything means toggling
 * a category on later is a re-render rather than a re-fetch.
 */
export function sponsorBlockRequestUrl(settings: SponsorBlockSettings, hashPrefix: string): string | null {
  if (!/^[0-9a-f]{4,32}$/.test(hashPrefix)) return null;
  const base = settings.apiUrl ?? SPONSORBLOCK_DEFAULT_API_URL;
  const categories = encodeURIComponent(JSON.stringify(SPONSOR_CATEGORIES));
  return `${base}/api/skipSegments/${hashPrefix}?categories=${categories}`;
}

// ---------------------------------------------------------------------------
// The skip decision
// ---------------------------------------------------------------------------

/**
 * A segment ending within this of the playhead is left alone: the transport samples at 4 Hz, so a
 * "skip" over the last quarter-second would land where playback was about to be anyway — and a
 * seek that near the boundary can re-sample *inside* the segment and fire twice.
 */
const SKIP_TAIL_SEC = 0.3;

/**
 * How far past one segment's end the next may start and still be taken in the same jump. The
 * database holds independently submitted ranges that abut within a frame or two; landing between
 * two of them for one poll tick would skip twice with a visible stutter instead of once.
 */
const CHAIN_GAP_SEC = 0.2;

export interface SponsorActionInput {
  readonly positionSec: number;
  readonly playing: boolean;
  /** A drag in progress — never act on a position the reader is only passing through. */
  readonly scrubbing: boolean;
  /** Segment UUIDs the reader has un-skipped or declined; never auto-acted on again. */
  readonly suppressed: readonly string[];
}

export type SponsorAction =
  /**
   * Jump to `toSec` and say so — the un-skip toast's `segment` names what was skipped, and
   * `segments` is every auto segment the jump passed. Un-skip must suppress **all of them**:
   * suppressing only the trigger of a chained jump leaves the overlapping second segment armed,
   * and it re-skips within seconds of the reader saying no (measured on `j0sv41bGx_g`, whose two
   * sponsor ranges overlap at 618.6 s).
   */
  | {
      readonly kind: 'skip';
      readonly toSec: number;
      readonly segment: ResolvedSponsorSegment;
      readonly segments: readonly ResolvedSponsorSegment[];
    }
  /** Inside a `toast` segment: offer the button, skip nothing. */
  | { readonly kind: 'offer'; readonly segment: ResolvedSponsorSegment }
  | { readonly kind: 'none' };

function inside(segment: ResolvedSponsorSegment, positionSec: number): boolean {
  return positionSec >= segment.startSec && positionSec < segment.endSec;
}

/**
 * The one decision the transport poll asks. Auto beats offer when both contain the playhead, and
 * an auto skip *chains*: the jump target is pushed through every further auto segment it lands in
 * (or nearly in — {@link CHAIN_GAP_SEC}), so back-to-back segments are one jump, not a stutter of
 * four. Suppressed segments are invisible to both arms — un-skipping is a decision about the
 * segment, and re-skipping it on the next tick would un-decide it.
 */
export function sponsorActionAt(
  segments: readonly ResolvedSponsorSegment[],
  input: SponsorActionInput,
): SponsorAction {
  if (!input.playing || input.scrubbing) return { kind: 'none' };
  const position = Number.isFinite(input.positionSec) ? input.positionSec : 0;
  const suppressed = new Set(input.suppressed);
  const actionable = segments.filter((segment) => !suppressed.has(segment.uuid));

  const auto = actionable.find(
    (segment) =>
      segment.behavior === 'auto' && inside(segment, position) && segment.endSec - position > SKIP_TAIL_SEC,
  );
  if (auto !== undefined) {
    let toSec = auto.endSec;
    // Bounded by the list length: each pass either extends `toSec` past another segment's end or
    // stops, and a segment can only extend it once.
    for (let extended = true; extended; ) {
      extended = false;
      for (const segment of actionable) {
        if (segment.behavior !== 'auto') continue;
        if (segment.startSec <= toSec + CHAIN_GAP_SEC && segment.endSec > toSec) {
          toSec = segment.endSec;
          extended = true;
        }
      }
    }
    // Everything the jump passes — the chain's members plus any auto segment lying inside it.
    const passed = actionable.filter(
      (segment) =>
        segment.behavior === 'auto' && segment.endSec > position && segment.startSec <= toSec,
    );
    return { kind: 'skip', toSec, segment: auto, segments: passed };
  }

  const offer = actionable.find((segment) => segment.behavior === 'toast' && inside(segment, position));
  return offer === undefined ? { kind: 'none' } : { kind: 'offer', segment: offer };
}

// ---------------------------------------------------------------------------
// The bar
// ---------------------------------------------------------------------------

/**
 * One painted stripe, **local to a timeline cell** exactly as `TimelineSegment`'s fractions are:
 * the bar is a flex row of chapter cells with real gaps between them, so a global fraction would
 * drift against the cells by the width of every gap it crosses. The renderer positions the stripe
 * inside the cell with the two fractions and paints it `color`.
 */
export interface SponsorBarMark {
  readonly uuid: string;
  readonly category: SponsorCategory;
  readonly color: string;
  readonly startFraction: number;
  readonly endFraction: number;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * The *drawn* segment under a position, or `null` between them. What the bar's knob and the hover
 * tooltip wear (2026-08-01): both mirror the stripes, so the gate is `showInBar` — a category
 * that paints nothing must not colour the knob either. First match wins; the list arrives sorted by
 * start, so overlaps resolve to the earlier submission the same way the stripes stack.
 */
export function sponsorMarkAt(
  segments: readonly ResolvedSponsorSegment[],
  positionSec: number,
): ResolvedSponsorSegment | null {
  if (!Number.isFinite(positionSec)) return null;
  return segments.find((segment) => segment.showInBar && inside(segment, positionSec)) ?? null;
}

/** The stripes that fall inside one cell. A segment spanning cells appears in each, clipped. */
export function sponsorMarksFor(
  cell: { readonly startSec: number; readonly endSec: number },
  segments: readonly ResolvedSponsorSegment[],
): readonly SponsorBarMark[] {
  const span = cell.endSec - cell.startSec;
  if (!(span > 0)) return [];
  const marks: SponsorBarMark[] = [];
  for (const segment of segments) {
    if (!segment.showInBar) continue;
    if (segment.endSec <= cell.startSec || segment.startSec >= cell.endSec) continue;
    marks.push({
      uuid: segment.uuid,
      category: segment.category,
      color: segment.color,
      startFraction: clamp01((segment.startSec - cell.startSec) / span),
      endFraction: clamp01((segment.endSec - cell.startSec) / span),
    });
  }
  return marks;
}

// ---------------------------------------------------------------------------
// Chapter badges
// ---------------------------------------------------------------------------

/** A chapter is badged when a category covers at least 75 % of it. */
export const CHAPTER_BADGE_COVERAGE = 0.75;

export interface ChapterSponsorBadge {
  readonly category: SponsorCategory;
  readonly color: string;
}

/**
 * One badge per chapter at most — the category covering the most of it, `null` for the rest.
 * Coverage is measured over the **union** of a category's segments (two half-covering sponsor
 * ranges are 100 %, not 200 %), against the chapter's span in the chapter list's own terms.
 */
export function chapterSponsorBadges(
  chapters: readonly Chapter[],
  segments: readonly ResolvedSponsorSegment[],
): readonly (ChapterSponsorBadge | null)[] {
  const eligible = segments.filter((segment) => segment.showInChapters);
  return chapters.map((chapter) => {
    const span = chapter.endSec - chapter.startSec;
    if (!(span > 0) || eligible.length === 0) return null;

    let best: { category: SponsorCategory; color: string; coverage: number } | null = null;
    for (const category of new Set(eligible.map((segment) => segment.category))) {
      const covered = coveredSeconds(
        chapter,
        eligible.filter((segment) => segment.category === category),
      );
      const coverage = covered / span;
      if (coverage >= CHAPTER_BADGE_COVERAGE && (best === null || coverage > best.coverage)) {
        const color = eligible.find((segment) => segment.category === category)?.color ?? '';
        best = { category, color, coverage };
      }
    }
    return best === null ? null : { category: best.category, color: best.color };
  });
}

/** Seconds of the chapter covered by the union of the given segments. */
function coveredSeconds(chapter: Chapter, segments: readonly ResolvedSponsorSegment[]): number {
  const clipped = segments
    .map((segment) => ({
      start: Math.max(segment.startSec, chapter.startSec),
      end: Math.min(segment.endSec, chapter.endSec),
    }))
    .filter((range) => range.end > range.start)
    .sort((a, b) => a.start - b.start);

  let covered = 0;
  let cursor = Number.NEGATIVE_INFINITY;
  for (const range of clipped) {
    const start = Math.max(range.start, cursor);
    if (range.end > start) {
      covered += range.end - start;
      cursor = range.end;
    }
  }
  return covered;
}
